import React from 'react';
import '../../theme-components/02-card.css';

/**
 * CornerAccents Component - Foundational Primitive #1b (CornerAccents)
 *
 * Renders L-shaped gold corner accents in all four corners of a card.
 * Uses CSS primitive classes from 02-card.css
 *
 * Per DO THIS/NOT THAT guide - cards MUST have corner accents.
 *
 * Features:
 * - Gold L-shaped corners (per DO THIS/NOT THAT guidelines)
 * - Positioned at card corners with configurable offset
 * - Configurable size
 *
 * Usage:
 * ```tsx
 * <div className="card-with-accents">
 *   <CornerAccents />
 *   {children}
 * </div>
 * ```
 *
 * All styling uses CSS primitives + CSS variables (no hardcoding).
 */

interface CornerAccentsProps {
  /**
   * Size of each corner accent in pixels
   * @default 18
   */
  size?: number;

  /**
   * Offset from card edges in pixels
   * @default 8
   */
  offset?: number;

  /**
   * Optional className for customization
   */
  className?: string;

  /**
   * Which corners to show
   * @default 'all' - shows all 4 corners
   * @example 'diagonal' - shows only top-left and bottom-right
   */
  corners?: 'all' | 'diagonal';
}

const CornerAccents: React.FC<CornerAccentsProps> = ({
  size = 18,
  offset = 8,
  className = '',
  corners = 'all'
}) => {
  const showTopLeft = true; // Always show in diagonal mode
  const showTopRight = corners === 'all';
  const showBottomLeft = corners === 'all';
  const showBottomRight = true; // Always show in diagonal mode

  return (
    <>
      {/* Top Left */}
      {showTopLeft && (
        <div
          className={`corner-accent-tl ${className}`}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            top: `${offset}px`,
            left: `${offset}px`
          }}
          aria-hidden="true"
        />
      )}

      {/* Top Right */}
      {showTopRight && (
        <div
          className={`corner-accent-tr ${className}`}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            top: `${offset}px`,
            right: `${offset}px`
          }}
          aria-hidden="true"
        />
      )}

      {/* Bottom Left */}
      {showBottomLeft && (
        <div
          className={`corner-accent-bl ${className}`}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            bottom: `${offset}px`,
            left: `${offset}px`
          }}
          aria-hidden="true"
        />
      )}

      {/* Bottom Right */}
      {showBottomRight && (
        <div
          className={`corner-accent-br ${className}`}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            bottom: `${offset}px`,
            right: `${offset}px`
          }}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default CornerAccents;
